import React, { useState, useCallback, useEffect } from 'react';
import InputField from './InputField';


interface RestrictedInputFieldProps {
  type?: string;
  placeholder: any;
  disabled?: boolean;
  id?: string;
  mandatory: any;
  value: any;
  language: any;
  onChange: any;
  onBlur?: () => void;
  touched?: boolean;
  errorMsg?: string;
  ignoreTouched?: boolean;
  childrenIcon?: JSX.Element;
  restrictedChars?: string[]; // New optional prop
}

/**
 * This is wrapper to `InputField.js`.
 * It adding restricted characters for the input.
 */
const RestrictedInputField: React.FC<RestrictedInputFieldProps> = ({
  value = '',
  onChange,
  restrictedChars = ['@', '!'], // Default restricted characters
  ...restProps
}) => {
  const [internalValue, setInternalValue] = useState<string>(value);

  // Create regex to filter out restricted characters
  const filterRestrictedChars = useCallback((inputValue: string) => {
    const regex = new RegExp(`[${restrictedChars.map(char => `\\${char}`).join('')}]`, 'g');
    return inputValue.replace(regex, '');
  }, [restrictedChars]);

  // Handle changes from the InputField
  const handleChange = useCallback((newValue: any) => {
    // Convert to string for filtering (in case value is not string)
    const stringValue = String(newValue || '');
    const filteredValue = filterRestrictedChars(stringValue);
    
    // Only update if the filtered value is different
    if (filteredValue !== internalValue) {
      setInternalValue(filteredValue);
      onChange(filteredValue); // Call the original onChange with clean value
    }
  }, [internalValue, onChange, filterRestrictedChars]);

  // Sync with external value changes (if controlled from parent)
  useEffect(() => {
    const stringValue = String(value || '');
    const filteredValue = filterRestrictedChars(stringValue);
    if (filteredValue !== internalValue) {
      setInternalValue(filteredValue);
    }
  }, [value, filterRestrictedChars, internalValue]);

  return (
    <InputField
      {...restProps} // Pass through all original props
      value={internalValue}
      onChange={handleChange}
    />
  );
};

export default RestrictedInputField;